import { OptionGroupDef } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import { createChooseOneGroups, withHeading } from "./initial-f2f-utils";

const YES_NO_OPTIONS = ["Yes", "No"];
const INDEPENDENT = "Independent";
const NEEDS_ASSISTANCE = "Needs assistance with";

/** "Independent" clears every other option and vice versa. */
const independentExclusive = (others: string[]): Record<string, string[]> => ({
  [INDEPENDENT]: others,
  ...Object.fromEntries(others.map((option) => [option, [INDEPENDENT]])),
});

const ADL_OPTIONS = [
  "Bathing",
  "Dressing",
  "Toileting",
  "Transferring",
  "Feeding",
  "Continence",
];
const IADL_OPTIONS = [
  "Medications",
  "Finances",
  "Transportation",
  "Shopping",
  "Meal prep",
  "Housekeeping",
  "Phone use",
];
// const MOBILITY_OPTIONS = [
// "Cane",
// "Walker",
// "Wheelchair",
// "Difficulty walking or climbing stairs",
// ];
const SAFE_ENVIRONMENT = "Safe environment";
const HOME_SAFETY_CONCERNS = [
  "Rugs",
  "Poor lighting",
  "No grab bars",
  "Clutter",
  "Other",
];
const NONE_OF_THESE = "None of these";
const SYMPTOM_OPTIONS = [
  "Dizziness standing up/getting out of bed",
  "Difficulty chewing or swallowing",
  "Difficulty breathing or shortness of breath",
  "Snoring or choking/gasping during sleep",
  "Confusion or forgetfulness",
];

// Each homebound question takes the provider's own details; no canned paragraph is attached to "Yes".

export const FUNCTIONAL_HOMEBOUND_GROUPS: OptionGroupDef[] = [
  {
    key: "adls",
    title: "ADLs (Activities of Daily Living)",
    options: [INDEPENDENT, NEEDS_ASSISTANCE],
    nested: { lead: NEEDS_ASSISTANCE, options: ADL_OPTIONS },
    exclusive: independentExclusive([NEEDS_ASSISTANCE, ...ADL_OPTIONS]),
    withDetails: false,
  },
  {
    key: "iadls",
    title: "IADLs (Instrumental Activities of Daily Living)",
    options: [INDEPENDENT, NEEDS_ASSISTANCE],
    nested: { lead: NEEDS_ASSISTANCE, options: IADL_OPTIONS },
    exclusive: independentExclusive([NEEDS_ASSISTANCE, ...IADL_OPTIONS]),
    withDetails: false,
  },
  // {
  // key: "mobility",
  // title: "Mobility / ambulation",
  // options: [INDEPENDENT, ...MOBILITY_OPTIONS],
  // exclusive: independentExclusive(MOBILITY_OPTIONS),
  // withDetails: false,
  // },
  ...withHeading(
    "Fall risk assessment",
    createChooseOneGroups(
      {
        fallsInPast12Months: "Falls in the past 12 months",
        injuryFromFall: "Injury from fall",
        fearOfFalling: "Fear of falling",
        gaitInstability: "Gait instability observed",
      },
      YES_NO_OPTIONS,
      true
    )
  ),
  {
    key: "homeSafety",
    title: "Home safety",
    options: [SAFE_ENVIRONMENT, ...HOME_SAFETY_CONCERNS],
    exclusive: {
      [SAFE_ENVIRONMENT]: HOME_SAFETY_CONCERNS,
      ...Object.fromEntries(
        HOME_SAFETY_CONCERNS.map((option) => [option, [SAFE_ENVIRONMENT]])
      ),
    },
    optionDetailPlaceholders: { Other: "Describe the concern" },
  },
  {
    key: "cognitiveImpact",
    title: "Cognitive impact on function",
    chooseOne: true,
    options: [
      "No impairment",
      "Mild difficulty (forgetfulness, needs reminders)",
      "Significant impairment (affects daily function)",
    ],
  },
  ...withHeading(
    "Sensory limitations",
    createChooseOneGroups(
      { hearing: "Hearing", vision: "Vision" },
      ["Normal", "Impaired"],
      true
    )
  ),
  {
    key: "symptoms",
    title: "Have you been experiencing any of the following?",
    options: [...SYMPTOM_OPTIONS, NONE_OF_THESE],
    exclusive: {
      [NONE_OF_THESE]: SYMPTOM_OPTIONS,
      ...Object.fromEntries(
        SYMPTOM_OPTIONS.map((option) => [option, [NONE_OF_THESE]])
      ),
    },
    withDetails: false,
    withFreeText: false,
  },
  {
    key: "transferAbility",
    title: "The ability to transfer from bed to chair and back to bed",
    options: [],
    withDetails: false,
  },
  ...withHeading(
    "Homebound status",
    createChooseOneGroups(
      {
        medicalConditionAffectingLeavingHome:
          "Medical condition affecting ability to leave home",
        inabilityToLeaveHome: "Inability to leave home",
        considerableAndTaxingEffort:
          "Leaving home requires considerable and taxing effort (e.g., taxi/assistance)",
      },
      YES_NO_OPTIONS
    ).map((group) => ({
      ...group,
      withFreeText: true,
    }))
  ),
  {
    key: "homeBoundStatus",
    title: "Homebound Status",
    heading: "Homebound status",
    withFreeText: false,
    withDetails: false,
    options: [
      "The patient's chronic multiple comorbid conditions collectively contribute to a homebound status, as leaving the home requires considerable and taxing effort, is medically contraindicated without assistance, and places the patient at increased risk for adverse health outcomes. Skilled home health services are therefore medically necessary and appropriate.",
    ],
  },
];
